import type { Route } from './types.js';

export class Router {
  private routes: Map<string, Route> = new Map();

  constructor() {
    this.handleGitHubPagesRedirect();
    this.initializeEventListeners();
  }

  private handleGitHubPagesRedirect(): void {
    // Check if we're being redirected from GitHub Pages 404
    const redirect = sessionStorage.redirect;
    if (redirect && redirect !== '') {
      sessionStorage.removeItem('redirect');
      // Restore the intended path
      const intendedPath = '/' + redirect;
      window.history.replaceState(null, '', intendedPath);
    }
  }

  private initializeEventListeners(): void {
    // Handle browser back/forward buttons
    window.addEventListener('popstate', () => {
      this.handleCurrentRoute();
    });

    // Handle navigation clicks
    document.addEventListener('click', (e: Event) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a[href]') as HTMLAnchorElement;
      
      if (link && this.isInternalLink(link.href)) {
        e.preventDefault();
        const path = new URL(link.href, window.location.origin).pathname;
        this.navigateTo(path);
      }
    });
  }

  private isInternalLink(href: string): boolean {
    try {
      const url = new URL(href, window.location.origin);
      // Only handle internal links that are route changes, not hash links
      return url.origin === window.location.origin && 
             url.pathname !== window.location.pathname &&
             !href.startsWith('#');
    } catch {
      return false;
    }
  }

  public addRoute(route: Route): void {
    this.routes.set(route.path, route);
  }

  public navigateTo(path: string): void {
    window.history.pushState(null, '', path);
    this.handleCurrentRoute();
  }

  public start(): void {
    this.handleCurrentRoute();
  }

  private handleCurrentRoute(): void {
    const currentPath = window.location.pathname;
    const route = this.routes.get(currentPath);
    
    if (route) {
      this.renderPage(route);
    } else {
      // If no exact match, render 404 or fallback to home
      const notFoundRoute = this.routes.get('/404') || this.routes.get('/');
      if (notFoundRoute) {
        this.renderPage(notFoundRoute);
      }
    }
  }

  private renderPage(route: Route): void {
    const appElement = document.getElementById('app');
    if (!appElement) return;

    // Render the page content
    appElement.innerHTML = route.component();

    // Update document title and meta
    document.title = route.title;
    this.updateMetaTags(route);

    // Scroll to top
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

    // Re-initialize page features
    this.initializePageFeatures();
    
    // Update active navigation
    setTimeout(() => {
      this.updateActiveNavigation(window.location.pathname);
    }, 0);
  }

  private updateMetaTags(route: Route): void {
    // Update meta description
    const metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (metaDesc && route.metaDescription) {
      metaDesc.content = route.metaDescription;
    }

    // Update canonical URL
    const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (canonical) {
      canonical.href = window.location.href;
    }
  }

  private updateActiveNavigation(currentPath: string): void {
    // Remove active state from all nav links
    document.querySelectorAll('.nav-link').forEach(link => {
      link.classList.remove('active');
    });

    // Add active state to current nav link
    const activeLink = document.querySelector(`a.nav-link[href="${currentPath}"]`);
    if (activeLink) {
      activeLink.classList.add('active');
    }
  }

  private initializePageFeatures(): void {
    // Re-initialize all interactive features
    const features = [
      'initializeInteractiveSteps',
      'initializeTwitterCarousel', 
      'initializeHamburgerMenu',
      'initializeSignupModal',
      'enhancedFormHandling'
    ];

    features.forEach(feature => {
      if (typeof (window as any)[feature] === 'function') {
        (window as any)[feature]();
      }
    });
  }
} 